import React, { useCallback, useEffect, useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isArray, has as xHas } from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';

import { ButtonGroup as DxButtonGroup } from 'devextreme-react/button-group';
import { queue } from 'async-es';
import canvasSize from 'canvas-size';
import numeral from 'numeral';
import Hammer from "hammerjs";

import { PlanViewContext } from './PlanViewContext';
import { useAuth } from '../../contexts/auth';

import { PaletteTool } from './Interactivity/Tooling';

const scaleAdjust = 96.0/72.0;

const pdfjsLib = require('pdfjs-dist/build/pdf');
//const _app_opts = require('pdfjs-dist/lib/web/app_options');

import progressSvg from './progress.svg'


const maxCanvasSize = {
    area: 10240 * 10240,
    width: 16384,
    height: 16384,
    magicFactor: 0.982
};

canvasSize.maxArea({
    onSuccess: function(width, height, benchmark) {
        maxCanvasSize.area = width * height;
        console.log('maxArea Success:', width, height, benchmark);
    }
});
canvasSize.maxWidth({
    onSuccess: function(width, height, benchmark) {
        console.log('maxWidth Success:', width, height, benchmark);
        maxCanvasSize.width = width;
    }
});
canvasSize.maxHeight({
    onSuccess: function(width, height, benchmark) {
        console.log('maxHeight Success:', width, height, benchmark);
        maxCanvasSize.height = height;
    }
});


const renderQueueWorkerFn = (task, callback) => {
    //console.log('rendering', task)

    task.renderQueue.currentRenderTask = task.renderTask;
    task.renderTask.onContinue = continueFn => {
        continueFn();
    };

    (task.onProgressStart() && task.onProgressStart());

    task.renderTask.promise
        .then(() => {
            task.renderQueue.currentRenderTask = null;
            callback(null);
            (task.onProgressEnd() && task.onProgressEnd());
            (task.onPageRendered && task.onPageRendered());
            //console.log('render done')
        })
        .catch(err => {
            task.renderQueue.currentRenderTask = null;
            if (err instanceof pdfjsLib.RenderingCancelledException) {
                callback(null);
                //console.log('render cancelled')
            }
            else {
                callback(err);
                console.error('render error', err)
            }
            //(task.onProgressEnd() && task.onProgressEnd());
            (task.onRenderCancelled && task.onRenderCancelled());
        })
};


const renderQueue = queue(renderQueueWorkerFn, 1);


function renderQueueDrain() {
    //console.log('render queue empty', this);
}


renderQueue.drain(renderQueueDrain.bind(renderQueue));


const dxThemeClassName=[
    'dx-theme-background-color',
    'dx-theme-text-color',
    'dx-theme-accent-as-border-color',
].join(' ');


const fToRad = Math.PI / 180.0;
const fToDeg = 1 / fToRad;


window.frustum = {
    frustums:{}
}
window.frustum.add = (id, func) => {
    window.frustum.frustums[id] = func;
}
window.frustum.remove = (id) => {
    delete window.frustum.frustums[id];
}
window.frustum.clear = () => {
    window.frustum.frustums = {};
}
window.frustum.draw = () => {
    window.clearFrustumCanvas && window.clearFrustumCanvas();
    for (const fid in window.frustum.frustums) {
        try {
            window.frustum.frustums[fid]();
        }
        catch (e) {
        }
    }
}


class Point2D {
    constructor(x, y) {
        this._x = x || 0;
        this._y = y || 0;
    }

    get x() { return this._x; }
    set x(value) { this._x = value; }

    get y() { return this._y; }
    set y(value) { this._y = value; }

    sub(other) {
        return new Vector2D(this.x - other.x, this.y - other.y);
    }
}


class Vector2D {
    constructor(x, y) {
        this._x = x || 0;
        this._y = y || 0;
    }

    get x() { return this._x; }
    set x(value) { this._x = value; }

    get y() { return this._y; }
    set y(value) { this._y = value; }

    get lengthSqrd() { return this._x*this._x + this._y*this._y; }

    get length() { return Math.sqrt(this.lengthSqrd); }

    normalize() {
        const l = Math.sqrt(this.lengthSqrd);
        this._x /= l;
        this._y /= l;
        return this;
    }

    getMultiplyScalar(d) {
        return new Vector2D(this._x, this._y).multiplyScalar(d);
    }

    multiplyScalar(d) {
        this._x *= d;
        this._y *= d;
        return this;
    }
}


class LineSegment2D {
    constructor(p, v) {
        if (p instanceof Point2D &&
            v instanceof Vector2D)
        {
            this._point = p;
            this._vector = v;
        }
        else {
            this._point = new Point2D();
            this._vector = new Vector2D();
        }
    }

    set point(value) { this._point = value; }
    get point() { return this._point; }

    set vector(value) { this._vector = value; }
    get vector() { return this._vector; }
}


class Ray2D {
    constructor(p, v) {
        if (p instanceof Point2D &&
            v instanceof Vector2D)
        {
            this._point = p;
            this._vector = v;
        }
        else {
            this._point = new Point2D();
            this._vector = new Vector2D();
        }
    }

    set point(value) { this._point = value; }
    get point() { return this._point; }

    set vector(value) { this._vector = value; }
    get vector() { return this._vector; }

    intersectWith(line) {
        const v = this.vector, lv = line.vector;
        const d = lv.x * v.y - v.x * lv.y;
        if (Math.abs(d) > v.lengthSqrd * 1e-10) {
            const d1 = lv.x * (line.point.y - this.point.y) - (line.point.x - this.point.x) * lv.y;
            const t1 = d1/d;
            if (t1 < 0) return false;

            const intPt = new Point2D(this.point.x+v.x*t1, this.point.y+v.y*t1);

            const vl = new Vector2D(intPt.x-line.point.x, intPt.y-line.point.y);
            const param = (lv.x * vl.x + lv.y * vl.y)/lv.lengthSqrd;

            if (param >= 0 && param <= 1)
                return intPt;
        }
        return false;
    }
}



function PlanViewAdvanced(props, exportsRef) {

    const {
        pdfPage,
        pdfPageViewport,
        pdfOptionalContentConfig,
        pdfVisibleGroups,
        pdfViewpoints,
        pdfTransform,
        pdfActiveTool,
        setActiveTool,
        actAsViewpointSelector,
        followSelected
    } = props;

    const {
        selectedViewpointId,
        setSelectedViewpointId
    } = useContext(PlanViewContext);

    const { keepalive } = useAuth();


    const canvasContainerRef = useRef(null);
    const canvasPdfRenderRef = useRef(null);
    const canvasDrawSourceRef = useRef(null);

    const statusBarRef = useRef(null);
    const statusTextRef = useRef(null);
    const statusCoordsRef = useRef(null);
    const progressIndicatorRef = useRef(null);
    const planDrawCanvasRef = useRef(null);
    const viewpointDrawCanvasRef = useRef(null);
    const frustumDrawCanvasRef = useRef(null);
    const jigDrawCanvasRef = useRef(null);
    const mouseEventListenerRef = useRef(null);
    const mouseEventListenerHammerRef = useRef(null);
    const resizeObserverRef = useRef();

    const renderRef = useRef({
        renderQueue:renderQueue,
    });


    const pdfViewDataRef = useRef({
        page:null,
        pageViewport:null,
        optionalContentConfig:null,

        viewPoints:[],
        viewPointSelected:0,
        viewPointsTrafo:new DOMMatrix([1,0,0,1,0,0]),

        toolPalette:null,
        //activeToolId:null,
        activeTool:null,
        activeToolPaletteButtons:null,

        minScale:0.2,
        maxScale:7,
        initialZoomExtents:true,

        scale:scaleAdjust,
        pinchScaleBase:scaleAdjust,
        pinchOffsetXBase:0,
        pinchOffsetYBase:0,
        offsetX:0,
        offsetY:0,
        viewTrMatrix: new DOMMatrix([1,0,0,1,0,0]),
        viewPointRadiusPx:6,
        crosshairSizePx:80,
        frustumSize:64,

        renderViewport:null,

        paintScale:1,

        isPanning:false,
        panStartAt:{x:0,y:0},
        panStartOffset:{x:0,y:0},
    });

    const resetPdfViewData = () => {
        console.log('resetPdfViewData')
        pdfViewDataRef.current.page=null;
        pdfViewDataRef.current.pageViewport=null;
        pdfViewDataRef.current.optionalContentConfig=null;

        pdfViewDataRef.current.viewPoints=[];
        pdfViewDataRef.current.viewPointSelected=0;
        pdfViewDataRef.current.viewPointsTrafo = new DOMMatrix([1,0,0,1,0,0]);

        pdfViewDataRef.current.toolPalette = null;

        //pdfViewDataRef.current.activeToolId=null;
        pdfViewDataRef.current.activeTool=null;
        pdfViewDataRef.current.activeToolPaletteButtons = null;

        pdfViewDataRef.current.minScale=0.2;
        //pdfViewDataRef.current.initialZoomExtents=true;

        pdfViewDataRef.current.scale=scaleAdjust;
        pdfViewDataRef.current.pinchScaleBase=scaleAdjust;
        pdfViewDataRef.current.pinchOffsetXBase=0;
        pdfViewDataRef.current.pinchOffsetYBase=0;
        pdfViewDataRef.current.offsetX=0;
        pdfViewDataRef.current.offsetY=0;
        pdfViewDataRef.current.viewTrMatrix= new DOMMatrix([1,0,0,1,0,0]);
        pdfViewDataRef.current.viewPointRadiusPx=6;
        pdfViewDataRef.current.crosshairSizePx=80;

        pdfViewDataRef.current.renderViewport=null;

        pdfViewDataRef.current.paintScale=1;

        pdfViewDataRef.current.isPanning=false;
        pdfViewDataRef.current.panStartAt={x:0,y:0};
        pdfViewDataRef.current.panStartOffset={x:0,y:0};
    }


    const [ activeCommand, setActiveCommand ] = useState(['pointer']);
    const [ activeToolState, setActiveToolState ] = useState({});


    const tolEq = (v0, v1) => {
        return Math.abs(v0 - v1) < 0.001;
    };

    const round = (v) => {
        return v;
        // return Math.floor(v*100.0)/100.0;
    }


    const calcZoomExtents = (pageViewport, viewSize) => {
        try {
            const sx = viewSize.width / pageViewport.width;
            const sy = viewSize.height / pageViewport.height;

            const sc = Math.min(sx, sy) * 0.97;
            const offX = (viewSize.width - pageViewport.width * sc) / 2.0;
            const offY = (viewSize.height - pageViewport.height * sc) / 2.0;

            return {
                scale:round(sc),
                offsetX:round(offX),
                offsetY:round(offY),
            };
        }
        catch {
            return {
                scale:scaleAdjust,
                offsetX:0,
                offsetY:0,
            };
        }
    };


    const setStatusText = (msg) => {
        if (!statusTextRef.current)
            return;
        statusTextRef.current.innerText = msg;
    };


    const setCoordsText = (msg) => {
        if (!statusCoordsRef.current)
            return;
        if (!props.showCoords)
            return;
        statusCoordsRef.current.innerText =
            msg
        ;
    };


    const onWheel = useCallback(async (evt) => {
        if (pdfViewDataRef.current.isPanning)
            return;
        if (!evt.ctrlKey && !evt.altKey && !evt.shiftKey && !evt.metaKey)
        {
            evt.preventDefault();

            let factor = (evt.deltaY < 0) ? 1.25 : 1/1.25;
            let newScale = pdfViewDataRef.current.scale * factor;
            newScale = round(
                Math.max(pdfViewDataRef.current.minScale,
                    Math.min(newScale, pdfViewDataRef.current.maxScale))
            );

            if (Math.abs(pdfViewDataRef.current.scale - newScale) < 0.01)
                return;


            const renderViewport = pdfViewDataRef.current.page.getViewport({
                scale:newScale,
                offsetX:0,
                offsetY:0,
            });

            // console.log(newScale, renderViewport)

            let zoomX = (evt.offsetX - pdfViewDataRef.current.offsetX); // pdfViewDataRef.current.scale;
            let zoomY = (evt.offsetY - pdfViewDataRef.current.offsetY); // pdfViewDataRef.current.scale;

            console.log(evt.offsetX, evt.offsetY)
            console.log(pdfViewDataRef.current.offsetX, pdfViewDataRef.current.offsetY)

            const dx = (zoomX /*- pdfViewDataRef.current.offsetX*/) * (factor - 1),
                  dy = (zoomY /*- pdfViewDataRef.current.offsetY*/) * (factor - 1);

            //const offsetX = (planDrawCanvasRef.current.width - renderViewport.width) / 2.0;
            //const offsetY = (planDrawCanvasRef.current.height - renderViewport.height) / 2.0;

            const localScaleX = renderViewport.width / canvasPdfRenderRef.current.width;
            const localScaleY = renderViewport.height / canvasPdfRenderRef.current.height;


            pdfViewDataRef.current.scale = newScale;
            pdfViewDataRef.current.offsetX -= dx;
            pdfViewDataRef.current.offsetY -= dy;
            pdfViewDataRef.current.renderViewport = renderViewport;
            pdfViewDataRef.current.paintScale *= (localScaleX+localScaleY)/2.0;

            if (renderViewport.width < planDrawCanvasRef.width) {
                pdfViewDataRef.current.offsetX = (planDrawCanvasRef.width - renderViewport.width) / 2;
            }
            if (renderViewport.height < planDrawCanvasRef.height) {
                pdfViewDataRef.current.offsetY = (planDrawCanvasRef.height - renderViewport.height) / 2;
            }

            calcViewTrMatrix();
            onPaint();
            onRenderPage();

            window.frustum?.draw();
        }
    },[ /*setPdf*/ ]);


    const onMouseMove = useCallback((evt) => {
        if (!pdfViewDataRef.current.renderViewport)
            return;

        let repaintPlan = false;
        let repaintViewpoints = true;

        let pixelX = evt.offsetX - pdfViewDataRef.current.offsetX;
        let pixelY = pdfViewDataRef.current.renderViewport.height - (evt.offsetY - pdfViewDataRef.current.offsetY);

        let scaledX = pixelX / pdfViewDataRef.current.scale;
        let scaledY = pixelY / pdfViewDataRef.current.scale;

        let paperX = Math.floor(scaledX / 72.0 * 2.54 * 10.0) / 10.0;
        let paperY = Math.floor(scaledY / 72.0 * 2.54 * 10.0) / 10.0;

        if (pdfViewDataRef.current.isPanning) {
            //if (!evt.ctrlKey)
                pdfViewDataRef.current.offsetX =
                    pdfViewDataRef.current.panStartOffset.x +
                    evt.pageX - pdfViewDataRef.current.panStartAt.x;
            //if (!evt.shiftKey)
                pdfViewDataRef.current.offsetY =
                    pdfViewDataRef.current.panStartOffset.y +
                    evt.pageY - pdfViewDataRef.current.panStartAt.y;
            calcViewTrMatrix();
            repaintPlan = true;
        }

        if (repaintPlan || repaintViewpoints) {
            onPaint({
                plan:repaintPlan,
                viewpoints:repaintViewpoints,
            });
            window.frustum?.draw();
        }

        let cursorShape = "auto";
        if (pdfViewDataRef.current.activeTool) {
            const res = pdfViewDataRef.current.activeTool.mouseMove({
                mouseAt: {x:evt.offsetX, y:evt.offsetY},
                cursorAt:new DOMPoint(scaledX, scaledY),
                viewData:pdfViewDataRef.current
            });
            if (res) {
                pdfViewDataRef.current.activeTool.draw({
                    canvasCtx:jigDrawCanvasRef.current.getContext("2d"),
                    drawViewpoint,
                    drawViewpointLabel,
                    cursorAt:new DOMPoint(scaledX, scaledY),
                    viewData:pdfViewDataRef.current
                });
            }
        }
        else if(actAsViewpointSelector) {
            const tr = pdfViewDataRef.current.viewPointsTrafo;

            const overPointIdx = pdfViewDataRef.current.viewPoints.findIndex(vp => {
                const svp = vp.matrixTransform(tr);
                const dx = svp.x - scaledX;
                const dy = svp.y - scaledY;
                const d = Math.sqrt(dx * dx + dy * dy) * pdfViewDataRef.current.scale;
                return d < pdfViewDataRef.current.viewPointRadiusPx;
            })

            if (overPointIdx > -1)
                cursorShape = "pointer"
        }
        document.body.style.cursor = cursorShape;

        if (statusBarRef.current && pdfViewDataRef.current.renderViewport) {
            // const cp = new DOMPoint(scaledX, scaledY, 0, 1);

            setCoordsText(
                // `${round(pdfViewDataRef.current.offsetX)}, ${round(pdfViewDataRef.current.offsetY)}` +
                // ' : ' +
                // `${numeral(pixelX).format('0.00')}, ${numeral(pixelY).format('0.00')}` +
                // ' : ' +
                `${numeral(scaledX).format('0.00')}, ${numeral(scaledY).format('0.00')}`
                // ' : ' +
                // `${numeral(paperX).format('0.00')}, ${numeral(paperY).format('0.00')}`
                // + ' : ' +
                // `${numeral(rpt.x).format('0.00')}, ${numeral(rpt.y).format('0.00')}`
                );
        }
    },[ ]);


    const onMouseDown = useCallback((evt) => {
        if (pdfViewDataRef.current.isPanning)
            return;

        if (!evt.ctrlKey && !evt.altKey && !evt.shiftKey && !evt.metaKey && evt.button === 1) {
            evt.preventDefault();

            //console.log(evt)

            pdfViewDataRef.current.isPanning = true;
            pdfViewDataRef.current.panStartAt = { x: evt.pageX, y: evt.pageY };
            pdfViewDataRef.current.panStartOffset = {
                x: pdfViewDataRef.current.offsetX,
                y: pdfViewDataRef.current.offsetY
            };
        }
    }, []);


    const onMouseUp = useCallback((evt) => {
        //console.log(evt)
        if (pdfViewDataRef.current.isPanning && evt.button === 1) {
            evt.preventDefault();
            pdfViewDataRef.current.isPanning = false;
        }
    }, []);


    const onMouseClick = useCallback((evt) => {
        keepalive();

        let pixelX = evt.offsetX - pdfViewDataRef.current.offsetX;
        let pixelY = pdfViewDataRef.current.renderViewport.height - (evt.offsetY - pdfViewDataRef.current.offsetY);

        let scaledX = pixelX / pdfViewDataRef.current.scale;
        let scaledY = pixelY / pdfViewDataRef.current.scale;

        if (pdfViewDataRef.current.activeTool) {
            const res = pdfViewDataRef.current.activeTool.mouseclick({
                button:evt.button,
                buttons:evt.buttons,
                altKey:evt.altKey,
                shiftKey:evt.shiftKey,
                ctrlKey:evt.ctrlKey,
                mouseAt: {x:evt.offsetX, y:evt.offsetY},
                cursorAt:new DOMPoint(scaledX, scaledY),
                viewData:pdfViewDataRef.current
            });
            if (res && pdfViewDataRef.current.activeTool) {
                pdfViewDataRef.current.activeTool.draw({
                    canvasCtx:jigDrawCanvasRef.current.getContext("2d"),
                    drawViewpoint,
                    drawViewpointLabel,
                    cursorAt:new DOMPoint(scaledX, scaledY),
                    viewData:pdfViewDataRef.current
                });
            }
        }
        else if(actAsViewpointSelector) {
            const tr = pdfViewDataRef.current.viewPointsTrafo;

            const overPointIdx = pdfViewDataRef.current.viewPoints.findIndex(vp => {
                const svp = vp.matrixTransform(tr);
                const dx = svp.x - scaledX;
                const dy = svp.y - scaledY;
                const d = Math.sqrt(dx * dx + dy * dy) * pdfViewDataRef.current.scale;
                return d < pdfViewDataRef.current.viewPointRadiusPx;
            })

            if (overPointIdx > -1) {
                setSelectedViewpointId(pdfViewDataRef.current.viewPoints[overPointIdx].id);
            }
        }
    }, [keepalive, setSelectedViewpointId]);


    const onKeyDown = useCallback((evt) => {
        const { key, altKey, ctrlKey, shiftKey } = evt;

        if (altKey && !ctrlKey && !shiftKey) {
            if (key === 'x' || key === 'X') {
                zoomExtents();
                return;
            }
        }
        if (altKey && !ctrlKey && !shiftKey) {
            if (key === 'r' || key === 'R') {
                onRenderPage();
                return;
            }
        }

        if (pdfViewDataRef.current.activeTool) {
            // if (pdfViewDataRef.current.activeTool.activeToolId === PaletteTool.POINTER_TOOL_ID) {
            //     if (key === 'Esc' || key === 'Escape') {
            //         pdfViewDataRef.current.activeTool.deactivate();
            //         setActiveTool(null);
            //         return;
            //     }
            // }

            const res = pdfViewDataRef.current.activeTool.keydown({
                evt
            });
            if (res) {
                pdfViewDataRef.current.activeTool.draw({
                    canvasCtx:jigDrawCanvasRef.current.getContext("2d"),
                    drawViewpoint,
                    drawViewpointLabel,
                    cursorAt:new DOMPoint(0, 0),
                    viewData:pdfViewDataRef.current
                });
            }
        }
    }, [
        //setActiveToolId
    ]);


    const onRenderPage = useCallback(() => {

        if (!pdfViewDataRef.current.page)
            return;

        (renderRef.current.renderQueue.currentRenderTask &&
         renderRef.current.renderQueue.currentRenderTask.cancel());

        new Promise((resolve, reject) => {

            //console.log('renderPage', pdfViewDataRef.current)

            canvasPdfRenderRef.current.width =
            canvasPdfRenderRef.current.height = 0;

            canvasPdfRenderRef.current.width = pdfViewDataRef.current.renderViewport.width + pdfViewDataRef.current.renderViewport.offsetX;
            canvasPdfRenderRef.current.height = pdfViewDataRef.current.renderViewport.height + pdfViewDataRef.current.renderViewport.offsetY;

            const clearRenderCanvas = () => {
                const ctx = canvasPdfRenderRef.current.getContext("2d");

                ctx.setTransform(1,0,0,1,0,0);
                ctx.clearRect(0,0,ctx.canvas.width,ctx.canvas.height);
            };

            clearRenderCanvas();

            let renderTask;
            try {
                renderRef.current.renderQueue.currentRenderTask =
                renderTask = pdfViewDataRef.current.page.render({
                    annotationStorage:undefined,
                    canvasContext:canvasPdfRenderRef.current.getContext("2d"),
                    viewport:pdfViewDataRef.current.renderViewport,
                    enableWebGL:true,
                    optionalContentConfigPromise: new Promise((resolve, reject) => {
                        //pdfViewDataRef.current.optionalContentConfig.setVisibility("10R", false);
                        resolve(pdfViewDataRef.current.optionalContentConfig);
                    }),
                });
            }
            catch (e) {
                console.error('render err', e)
            }

            //progressIndicatorRef.current.style.display = 'flex';

            renderRef.current.renderQueue.push({
                renderTask,
                renderQueue:renderRef.current.renderQueue,
                onProgressStart:() => {
                    //console.log('onProgressStart')
                    progressIndicatorRef.current.style.display = 'flex';
                },
                onProgressEnd:() => {
                    progressIndicatorRef.current.style.display = 'none';
                    //console.log('onProgressEnd')
                },
                onPageRendered:() => {
                    canvasDrawSourceRef.current.width =
                    canvasDrawSourceRef.current.height = 0;
                    canvasDrawSourceRef.current.width = canvasPdfRenderRef.current.width;
                    canvasDrawSourceRef.current.height = canvasPdfRenderRef.current.height;

                    const ctx = canvasDrawSourceRef.current.getContext("2d");

                    ctx.setTransform(1, 0, 0, 1, 0, 0);
                    ctx.clearRect(0,0,ctx.canvas.width, ctx.canvas.height);

                    ctx.drawImage(canvasPdfRenderRef.current,
                        0,
                        0,
                        canvasDrawSourceRef.current.width,
                        canvasDrawSourceRef.current.height
                    );

                    pdfViewDataRef.current.paintScale = 1;

                    clearRenderCanvas();

                    onPaint();
                },
                onRenderCancelled: async () => {
                    clearRenderCanvas();
                }
            });

            resolve();
        })
        .catch(err => {
            console.error('err', err)
        })
        ;
    }, []);


    const calcViewTrMatrix = useCallback(() => {
        const t1_ViewScale = new DOMMatrix([
            pdfViewDataRef.current.scale,0,
            0,pdfViewDataRef.current.scale,
            0,0]);

        const t2_ViewMirror = new DOMMatrix([
            1,0,
            0,-1,
            0,pdfViewDataRef.current.pageViewport.height * pdfViewDataRef.current.scale]);

        const t3_ViewOffset = new DOMMatrix([
            1,0,
            0,1,
            pdfViewDataRef.current.offsetX,pdfViewDataRef.current.offsetY]);

        pdfViewDataRef.current.viewTrMatrix = t3_ViewOffset.multiply(t2_ViewMirror).multiply(t1_ViewScale);
    }, []);


    const viewPointDefaultDrawProps = {
        background:'#FFFFFF',
        //fill:'#00AA00',
        fill:'#33FF33',
        stroke:'#004000'
    };


    const drawViewpoint = (canvas, viewPoint, viewTrafo, options) => {

        let { background, fill, stroke, drawAsSelected } = options || viewPointDefaultDrawProps;
        if (!background) background = viewPointDefaultDrawProps.background;
        if (!fill) fill = viewPointDefaultDrawProps.fill;
        if (!stroke) stroke = viewPointDefaultDrawProps.stroke;

        const svp = viewPoint.matrixTransform(viewTrafo);

        canvas.fillStyle = background;
        canvas.beginPath();
        canvas.arc(svp.x, svp.y, pdfViewDataRef.current.viewPointRadiusPx + 2, 0, Math.PI * 2);
        canvas.fill();

        canvas.fillStyle = fill;
        canvas.strokeStyle = stroke;
        canvas.lineWidth = 2;

        canvas.beginPath();
        canvas.arc(svp.x, svp.y, pdfViewDataRef.current.viewPointRadiusPx, 0, Math.PI * 2);
        canvas.fill();
        canvas.stroke();

        if (drawAsSelected) {
            canvas.lineWidth = 3;

            canvas.beginPath();
            canvas.arc(svp.x, svp.y, pdfViewDataRef.current.viewPointRadiusPx + 3.5, 0, Math.PI * 2);
            canvas.stroke();
        }
    };


    const drawViewpointLabel = (canvas, viewPoint, viewTrafo, options) => {

        const { background, fill, font } = options || {
            background:'#FFFFFF',
            fill:'#000000',
            font:`bold ${14}px sans-serif`
        };

        const svp = viewPoint.matrixTransform(viewTrafo);

        canvas.font = font;

        const ts = canvas.measureText(viewPoint.name);
        const tx = svp.x - ts.width / 2;
        const ty = (svp.y - 12);

        canvas.fillStyle = background;
        canvas.fillText(viewPoint.name, tx - 1, ty - 1);
        canvas.fillText(viewPoint.name, tx + 1, ty - 1);
        canvas.fillText(viewPoint.name, tx - 1, ty + 1);
        canvas.fillText(viewPoint.name, tx + 1, ty + 1);

        canvas.fillStyle = fill;
        canvas.fillText(viewPoint.name, tx, ty);
    };


    const drawFrustumAt = function(canvas, viewPoint, viewTrafo, hfov, camViewAngle, color = [51,255,51]) {
    //const drawFrustumAt = function(canvas, viewPoint, viewTrafo, hfov, camViewAngle, color = [64,160,192]) {
        const svp = viewPoint.matrixTransform(viewTrafo);

        const a0 = fToRad * (camViewAngle - hfov / 2);
        const a1 = fToRad * (camViewAngle + hfov / 2);
        const cx0 = svp.x + (Math.cos(a0) * pdfViewDataRef.current.frustumSize);
        const cy0 = svp.y + (Math.sin(a0) * pdfViewDataRef.current.frustumSize);
        const cx1 = svp.x + (Math.cos(a1) * pdfViewDataRef.current.frustumSize);
        const cy1 = svp.y + (Math.sin(a1) * pdfViewDataRef.current.frustumSize);

        const camFrustum = new Path2D();
        camFrustum.moveTo(svp.x, svp.y);
        camFrustum.arc(svp.x, svp.y, pdfViewDataRef.current.frustumSize,a0,a1);
        camFrustum.closePath();

        const camViewGrad = canvas.createRadialGradient(
            svp.x, svp.y,0,
            svp.x, svp.y,
            pdfViewDataRef.current.frustumSize);
        camViewGrad.addColorStop(0.0, `rgba(${color[0]},${color[1]},${color[2]},0.85)`);
        camViewGrad.addColorStop(0.3, `rgba(${color[0]},${color[1]},${color[2]},0.65)`);
        camViewGrad.addColorStop(1.0, `rgba(${color[0]},${color[1]},${color[2]},0.30)`);

        canvas.shadowBlur = 5;
        canvas.shadowColor = `rgba(${color[0]},${color[1]},${color[2]},1)`;
        canvas.fillStyle = camViewGrad;
        canvas.fill(camFrustum);
        canvas.shadowBlur = canvas.shadowOffsetX = canvas.shadowOffsetY = 0;


        canvas.strokeStyle = '#186618';
        //canvas.strokeStyle = '#000000';
        canvas.lineWidth = 2;
        canvas.lineCap = 'round';
        canvas.beginPath();
        canvas.moveTo(cx0,cy0);
        canvas.lineTo(svp.x, svp.y);
        canvas.lineTo(cx1,cy1);
        canvas.stroke();
    }


    window.clearFrustumCanvas = () => {
        if (!frustumDrawCanvasRef.current)
            return;

        const frustumCtx = frustumDrawCanvasRef.current.getContext("2d");

        frustumCtx.setTransform(1, 0, 0, 1, 0, 0);
        frustumCtx.clearRect(0, 0, frustumCtx.canvas.width, frustumCtx.canvas.height);
    }


    window.drawFrustum = (posX, posY, hfov, camViewAngle, clearCanvas = true) => {
        if (!frustumDrawCanvasRef.current)
            return;

        const frustumCtx = frustumDrawCanvasRef.current.getContext("2d");

        frustumCtx.setTransform(1, 0, 0, 1, 0, 0);
        if (clearCanvas)
            frustumCtx.clearRect(0, 0, frustumCtx.canvas.width, frustumCtx.canvas.height);

        if (pdfViewDataRef.current.viewPointSelected < 0)
            return;
        if (hfov === undefined)
            return;

        if (isArray(pdfViewDataRef.current.viewPoints)) {
            const idx = pdfViewDataRef.current.viewPoints.findIndex(vp => vp.id === pdfViewDataRef.current.viewPointSelected);
            if (idx >= 0) {
                const tr = pdfViewDataRef.current.viewTrMatrix.multiply(pdfViewDataRef.current.viewPointsTrafo);
                const vp = pdfViewDataRef.current.viewPoints[idx];
                drawFrustumAt(frustumCtx, vp, tr, hfov, 360 - camViewAngle);
                drawViewpoint(frustumCtx, vp, tr,  {
                    drawAsSelected:pdfViewDataRef.current.viewPointSelected === vp.id
                });
                drawViewpointLabel(frustumCtx, vp, tr);
            }
        }
    }


    window.drawFreeFrustumAt = (posX, posY, hfov, camViewAngle, clearCanvas = true) => {
        if (!frustumDrawCanvasRef.current)
            return;

        const frustumCtx = frustumDrawCanvasRef.current.getContext("2d");

        frustumCtx.setTransform(1, 0, 0, 1, 0, 0);
        if (clearCanvas)
            frustumCtx.clearRect(0, 0, frustumCtx.canvas.width, frustumCtx.canvas.height);

        //const tr = pdfViewDataRef.current.viewTrMatrix.multiply(pdfViewDataRef.current.viewPointsTrafo);
        const tr = pdfViewDataRef.current.viewTrMatrix;
        const vp = new DOMPoint(posX, posY);
        drawFrustumAt(frustumCtx, vp, tr, hfov, 360 - camViewAngle, [192, 32, 192]);

        const a = (360 - camViewAngle) * Math.PI / 180;
        const sina = Math.sin(a);
        const cosa = Math.cos(a);

        const svp = vp.matrixTransform(tr);

        frustumCtx.fillStyle = 'black';
        frustumCtx.setTransform(
            0.25 * cosa, 0.25 * sina,
            0.25 * -sina, 0.25 * cosa,
            svp.x, svp.y);
        frustumCtx.beginPath();
        const dx = 70;
        const dy = 17.55;
        frustumCtx.moveTo (3.5 - dx, 0 - dy);
        frustumCtx.ellipse(3.5 - dx, 3.5 - dy, 3.5, 3.5, 0, Math.PI*3/2, Math.PI, true);
        frustumCtx.lineTo (0 - dx, 31.6 - dy);
        frustumCtx.ellipse(3.5 - dx, 31.6 - dy, 3.5, 3.5, 0, Math.PI, Math.PI/2, true);
        frustumCtx.lineTo (43.5 - dx, 35.1 - dy);
        frustumCtx.ellipse(43.5 - dx, 31.6 - dy, 3.5, 3.5, 0, Math.PI*3/2, 0, true);
        frustumCtx.lineTo (47 - dx, 23.1 - dy);
        frustumCtx.lineTo (69.6 - dx, 35.1 - dy);
        frustumCtx.lineTo (69.6 - dx, 0 - dy);
        frustumCtx.lineTo (47 - dx, 12 - dy);
        //frustumCtx.ellipse(36.6 - dx, 17.5 - dy, 37.4, 37.4, 0, Math.PI*2-0.48759513, 0.48759513, true);
        frustumCtx.lineTo (47 - dx, 3.5 - dy);
        frustumCtx.ellipse(43.5 - dx, 3.5 - dy, 3.5, 3.5, 0, 0, Math.PI*3/2, true);
        frustumCtx.fill();
        frustumCtx.setTransform(1, 0, 0, 1, 0, 0);
    }


    const zoomExtents = () => {
        if (!pdfViewDataRef.current || !pdfViewDataRef.current.page)
            return;

        const pageViewport = pdfViewDataRef.current.page.getViewport({ scale:1, offsetX:0, offsetY:0 });
        const ext = calcZoomExtents(pageViewport, {
            width:planDrawCanvasRef.current.width,
            height:planDrawCanvasRef.current.height
        });
        const renderViewport = pdfViewDataRef.current.page.getViewport({
            scale:ext.scale,
            offsetX:0,
            offsetY:0,
        });

        pdfViewDataRef.current.scale = ext.scale;
        pdfViewDataRef.current.paintScale = ext.scale;
        pdfViewDataRef.current.minScale = ext.scale * 0.5;
        pdfViewDataRef.current.offsetX = ext.offsetX;
        pdfViewDataRef.current.offsetY = ext.offsetY;
        pdfViewDataRef.current.renderViewport = renderViewport;

        calcViewTrMatrix();

        onPaint();
        onRenderPage();

        window.frustum?.draw();
    }


    const onPaint = useCallback((options = { full:true }) => {
        if (!pdfViewDataRef.current)
            return;
        if (!pdfViewDataRef.current.pageViewport)
            return;

        const { full, plan, viewpoints } = options;
        const shadowSize = 8;

        if (full || plan) {
            const ctx = planDrawCanvasRef.current.getContext("2d");

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            //ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

            ctx.fillStyle = '#666666';
            ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

            ctx.fillStyle = '#2A2A2A';
            ctx.fillRect(pdfViewDataRef.current.offsetX + shadowSize,
                pdfViewDataRef.current.offsetY + shadowSize,
                canvasDrawSourceRef.current.width * pdfViewDataRef.current.paintScale,
                canvasDrawSourceRef.current.height * pdfViewDataRef.current.paintScale);

            ctx.drawImage(canvasDrawSourceRef.current,
                pdfViewDataRef.current.offsetX,
                pdfViewDataRef.current.offsetY,
                canvasDrawSourceRef.current.width * pdfViewDataRef.current.paintScale,
                canvasDrawSourceRef.current.height * pdfViewDataRef.current.paintScale
            );

            ctx.strokeStyle = '#1A1A1A';
            ctx.beginPath();
            ctx.rect(pdfViewDataRef.current.offsetX,
                pdfViewDataRef.current.offsetY,
                canvasDrawSourceRef.current.width * pdfViewDataRef.current.paintScale,
                canvasDrawSourceRef.current.height * pdfViewDataRef.current.paintScale
            );
            ctx.stroke();

            ctx.fillStyle = 'rgba(0,0,0,0)';
            ctx.strokeStyle = '#000000';
        }

        calcViewTrMatrix();

        const toolOverrides =
            pdfViewDataRef.current.activeTool &&
            pdfViewDataRef.current.activeTool.environment().drawsViewpoints
        ;

        if (full || viewpoints) {

            const vpCtx = viewpointDrawCanvasRef.current.getContext("2d");

            vpCtx.setTransform(1, 0, 0, 1, 0, 0);
            vpCtx.clearRect(0, 0, vpCtx.canvas.width, vpCtx.canvas.height);

            if (toolOverrides) {
                pdfViewDataRef.current.activeTool.draw({
                    canvasCtx:jigDrawCanvasRef.current.getContext("2d"),
                    drawViewpoint,
                    drawViewpointLabel,
                    cursorAt:null,
                    viewData:pdfViewDataRef.current
                });
            }
            else {
                const jigCtx = jigDrawCanvasRef.current.getContext("2d");

                jigCtx.setTransform(1, 0, 0, 1, 0, 0);
                jigCtx.clearRect(0, 0, jigCtx.canvas.width, jigCtx.canvas.height);

                if (isArray(pdfViewDataRef.current.viewPoints)) {
                    const tr = pdfViewDataRef.current.viewTrMatrix.multiply(pdfViewDataRef.current.viewPointsTrafo);

                    pdfViewDataRef.current.viewPoints.forEach((vp, idx) => {
                        drawViewpoint(vpCtx, vp, tr, {
                            drawAsSelected:pdfViewDataRef.current.viewPointSelected === vp.id
                        });
                    });

                    pdfViewDataRef.current.viewPoints.forEach(vp => {
                        drawViewpointLabel(vpCtx, vp, tr)
                    });
                }
            }
        }

        if (window.frustum && window.frustum.frustums?.length > 0) {
            window.frustum.draw();
        }
        else {
            window.redrawFrustum && window.redrawFrustum();
            //window.redrawFreeFrustum && window.redrawFreeFrustum();
        }
    }, []);


    const onResize = useCallback((width, height) => {
        planDrawCanvasRef.current.width =
        viewpointDrawCanvasRef.current.width =
        frustumDrawCanvasRef.current.width =
        jigDrawCanvasRef.current.width =
        width;

        planDrawCanvasRef.current.height =
        viewpointDrawCanvasRef.current.height =
        frustumDrawCanvasRef.current.height =
        jigDrawCanvasRef.current.height =
        height;

        if (pdfViewDataRef.current.initialZoomExtents) {
            pdfViewDataRef.current.initialZoomExtents = false;

            zoomExtents();
        }
        else {
            onPaint();
        }
    }, []);


    const onActiveCommandChanged = useCallback(e => {
        if(e.name === 'selectedItemKeys') {
            setActiveCommand(e.value);
        }
    }, [setActiveCommand]);


    useEffect(() => {
        if (isArray(activeCommand) && activeCommand.length > 0) {
            if (pdfViewDataRef.current.activeTool) {
                pdfViewDataRef.current.activeTool.activeToolId = activeCommand[0];
            }
        }
    }, [activeCommand]);


    useEffect(() => {

        if (!pdfPage)
            return;
        if (!pdfPageViewport)
            return;
        if (!pdfOptionalContentConfig)
            return;

        //console.log('pdf',pdf)

        const samePage =
            pdfViewDataRef.current.page &&
            pdfViewDataRef.current.page === pdfPage;

        pdfViewDataRef.current.page = pdfPage;
        pdfViewDataRef.current.pageViewport = pdfPageViewport;
        pdfViewDataRef.current.optionalContentConfig = pdfOptionalContentConfig;
        pdfViewDataRef.current.viewPoints = pdfViewpoints;
        pdfViewDataRef.current.viewPointsTrafo = pdfTransform;

        if (!samePage) {
            const pageViewport = pdfPage.getViewport({ scale:1, offsetX:0, offsetY:0 });
            // console.log(pageViewport)

            const maxC = maxCanvasSize.area / 3;

            let maxScale = maxC / (pageViewport.width * pageViewport.height);

            if (pageViewport.width * maxScale > maxCanvasSize.width) {
                maxScale = maxCanvasSize.width / pageViewport.width;
            }
            if (pageViewport.height * maxScale > maxCanvasSize.height) {
                maxScale = maxCanvasSize.height / pageViewport.height;
            }
            maxScale *= maxCanvasSize.magicFactor;

            const ext = calcZoomExtents(pageViewport, {
                width:planDrawCanvasRef.current.width,
                height:planDrawCanvasRef.current.height
            });
            if (ext.scale > maxScale)
                ext.scale = maxScale;

            const renderViewport = pdfPage.getViewport({
                scale:ext.scale,
                offsetX:0,
                offsetY:0,
            });

            pdfViewDataRef.current.scale = ext.scale;
            pdfViewDataRef.current.minScale = ext.scale * 0.5;
            pdfViewDataRef.current.maxScale = maxScale;
            pdfViewDataRef.current.offsetX = ext.offsetX;
            pdfViewDataRef.current.offsetY = ext.offsetY;
            pdfViewDataRef.current.renderViewport = renderViewport;

            calcViewTrMatrix();
        }

        const order = pdfOptionalContentConfig.getOrder() || [];
        const currentState = order.filter(id => pdfOptionalContentConfig.getGroup(id).visible);
        const newState = isEmpty(pdfVisibleGroups) ? order : pdfVisibleGroups;

        const layerVisibilitySame =
            currentState.length === newState.length &&
            currentState.reduce((acc, cv) => acc && newState.includes(cv), true);

        if (!layerVisibilitySame)
            order.forEach(id => pdfOptionalContentConfig.setVisibility(id, newState.includes(id)));

        if (pdfViewDataRef.current.activeTool !== pdfActiveTool) {
            if (pdfViewDataRef.current.activeTool)
                pdfViewDataRef.current.activeTool.deactivate({});

            pdfViewDataRef.current.activeTool = pdfActiveTool;
            if (pdfViewDataRef.current.activeTool) {
                const toolState =
                pdfViewDataRef.current.activeTool.activate({
                    viewData:pdfViewDataRef.current,
                    viewPoints:pdfViewDataRef.current.viewPoints,
                    viewPointsTrafo:pdfViewDataRef.current.viewPointsTrafo,
                    canvasCtx:jigDrawCanvasRef.current.getContext("2d"),
                    setActiveCommand:commandId => setActiveCommand([ commandId ]),
                    setStatusText,
                    repaint:onPaint,
                    deactivateTool:() => setActiveTool(null)
                });
                if (toolState) {
                    setActiveToolState(toolState);
                }
            }

            pdfViewDataRef.current.activeToolPaletteButtons = null;
            if (pdfViewDataRef.current.activeTool) {
                const customPalette = pdfViewDataRef.current.activeTool.environment().palette;
                if (isArray(customPalette) && customPalette.length > 0) {
                    pdfViewDataRef.current.activeToolPaletteButtons =
                        [
                            PaletteTool.POINTER_TOOL,
                            ...customPalette
                        ];
                }
            }
        }

        if (!samePage || !layerVisibilitySame) {
            onRenderPage();
        }
        else {
            onPaint();
        }

        return () => {
            // resetPdfViewData();
        }

    },[pdfPage,
       pdfPageViewport,
       pdfOptionalContentConfig,
       pdfVisibleGroups,
       pdfViewpoints,
       pdfTransform,
       pdfActiveTool,
       setActiveTool,
       setActiveToolState,
       onPaint
    ]);


    useEffect(() => {
        if (!mouseEventListenerRef.current)
            return;

        if (window.isTablet) {
            mouseEventListenerHammerRef.current = new Hammer(mouseEventListenerRef.current, {

            });

            mouseEventListenerHammerRef.current.get('pan').set({
                threshold: 10,
                direction: Hammer.DIRECTION_ALL
            });

            mouseEventListenerHammerRef.current.on("panstart", hammerEvt => {
                if (!pdfViewDataRef.current.renderViewport)
                    return;

                console.log('panstart')

                pdfViewDataRef.current.isPanning = true;
                pdfViewDataRef.current.panStartAt = { x: hammerEvt.srcEvent.pageX, y: hammerEvt.srcEvent.pageY };
                pdfViewDataRef.current.panStartOffset = {
                    x: pdfViewDataRef.current.offsetX,
                    y: pdfViewDataRef.current.offsetY
                };
            });

            mouseEventListenerHammerRef.current.on("panend", hammerEvt => {
                if (!pdfViewDataRef.current.renderViewport)
                    return;

                console.log('panend')

                pdfViewDataRef.current.isPanning = false;
            });

            mouseEventListenerHammerRef.current.on("pan", hammerEvt => {
                if (!pdfViewDataRef.current.renderViewport)
                    return;

                pdfViewDataRef.current.offsetX =
                    pdfViewDataRef.current.panStartOffset.x +
                    hammerEvt.srcEvent.pageX - pdfViewDataRef.current.panStartAt.x;
                pdfViewDataRef.current.offsetY =
                    pdfViewDataRef.current.panStartOffset.y +
                    hammerEvt.srcEvent.pageY - pdfViewDataRef.current.panStartAt.y;
                calcViewTrMatrix();

                onPaint({
                    plan:true,
                    viewpoints:true,
                });
                window.frustum?.draw();
            });


            mouseEventListenerHammerRef.current.get('pinch').set({
                enable: true,
            });

            mouseEventListenerHammerRef.current.on("pinchstart", hammerEvt => {
                if (!pdfViewDataRef.current.renderViewport)
                    return;

                console.log('pinchstart', pdfViewDataRef.current.scale)

                pdfViewDataRef.current.pinchScaleBase = pdfViewDataRef.current.scale;
                pdfViewDataRef.current.pinchOffsetXBase = pdfViewDataRef.current.offsetX;
                pdfViewDataRef.current.pinchOffsetYBase = pdfViewDataRef.current.offsetY;
            });

            mouseEventListenerHammerRef.current.on("pinch", hammerEvt => {
                if (!pdfViewDataRef.current.renderViewport)
                    return;

                let factor = hammerEvt.scale;

                let newScale = pdfViewDataRef.current.pinchScaleBase * factor;
                newScale = round(
                    Math.max(pdfViewDataRef.current.minScale,
                        Math.min(newScale, pdfViewDataRef.current.maxScale))
                );

                if (Math.abs(pdfViewDataRef.current.scale - newScale) < 0.01)
                    return;

                const renderViewport = pdfViewDataRef.current.page.getViewport({
                    scale:newScale,
                    offsetX:0,
                    offsetY:0,
                });

                let zoomX = (hammerEvt.center.x - pdfViewDataRef.current.pinchOffsetXBase); // pdfViewDataRef.current.scale;
                let zoomY = (hammerEvt.center.y - pdfViewDataRef.current.pinchOffsetYBase); // pdfViewDataRef.current.scale;

                const dx = (zoomX) * (factor - 1),
                      dy = (zoomY) * (factor - 1);

                pdfViewDataRef.current.scale = newScale;
                pdfViewDataRef.current.offsetX = pdfViewDataRef.current.pinchOffsetXBase - dx/2;
                pdfViewDataRef.current.offsetY = pdfViewDataRef.current.pinchOffsetYBase - dy/2;
                pdfViewDataRef.current.renderViewport = renderViewport;
                pdfViewDataRef.current.paintScale = factor;

                if (renderViewport.width < planDrawCanvasRef.width) {
                    pdfViewDataRef.current.offsetX = (planDrawCanvasRef.width - renderViewport.width) / 2;
                }
                if (renderViewport.height < planDrawCanvasRef.height) {
                    pdfViewDataRef.current.offsetY = (planDrawCanvasRef.height - renderViewport.height) / 2;
                }

                calcViewTrMatrix();
                onPaint();

                window.frustum?.draw();
            });

            mouseEventListenerHammerRef.current.on("pinchend", hammerEvt => {
                if (!pdfViewDataRef.current.renderViewport)
                    return;

                pdfViewDataRef.current.pinchScaleBase = pdfViewDataRef.current.scale;

                onRenderPage();

                console.log('pinchend', pdfViewDataRef.current.scale)
            });

            mouseEventListenerHammerRef.current.get('pinch').requireFailure(mouseEventListenerHammerRef.current.get('pan'));

            if (actAsViewpointSelector)
            mouseEventListenerHammerRef.current.on("tap", hammerEvt => {
                if (!pdfViewDataRef.current.renderViewport)
                    return;

                console.log('tap', hammerEvt)

                keepalive();

                let pixelX = hammerEvt.srcEvent.offsetX - pdfViewDataRef.current.offsetX;
                let pixelY = pdfViewDataRef.current.renderViewport.height - (hammerEvt.srcEvent.offsetY - pdfViewDataRef.current.offsetY);

                let scaledX = pixelX / pdfViewDataRef.current.scale;
                let scaledY = pixelY / pdfViewDataRef.current.scale;

                const tr = pdfViewDataRef.current.viewPointsTrafo;

                const overPointIdx = pdfViewDataRef.current.viewPoints.findIndex(vp => {
                    const svp = vp.matrixTransform(tr);
                    const dx = svp.x - scaledX;
                    const dy = svp.y - scaledY;
                    const d = Math.sqrt(dx * dx + dy * dy) * pdfViewDataRef.current.scale;
                    return d < pdfViewDataRef.current.viewPointRadiusPx;
                })

                if (overPointIdx > -1) {
                    setSelectedViewpointId(pdfViewDataRef.current.viewPoints[overPointIdx].id);
                }
            });

            console.log('mouseEventListenerHammerRef', mouseEventListenerHammerRef.current)
        }
        else {
            mouseEventListenerRef.current.addEventListener("wheel", onWheel, { passive: false });

            mouseEventListenerRef.current.addEventListener("mousemove", onMouseMove);
            mouseEventListenerRef.current.addEventListener("mousedown", onMouseDown);
            mouseEventListenerRef.current.addEventListener("mouseup", onMouseUp);

            mouseEventListenerRef.current.addEventListener("click", onMouseClick);

            window.addEventListener("keydown", onKeyDown);
        }

        return () => {

            if (window.isTablet) {
            }
            else {
                window.removeEventListener("keydown", onKeyDown);

                mouseEventListenerRef.current.removeEventListener("click", onMouseClick);

                mouseEventListenerRef.current.removeEventListener("mousedown", onMouseDown);
                mouseEventListenerRef.current.removeEventListener("mouseup", onMouseUp);
                mouseEventListenerRef.current.removeEventListener("mousemove", onMouseMove);

                mouseEventListenerRef.current.removeEventListener("wheel", onWheel);
            }
        };
    }, [onWheel, onMouseMove, onMouseDown, onMouseUp, onMouseClick, onKeyDown ]);


    useEffect(() => {
        resizeObserverRef.current = new ResizeObserver(entries => {
            for (let entry of entries)
            {
                let w = Math.ceil(entry.contentRect.width);
                let h = Math.ceil(entry.contentRect.height);

                onResize(w, h);

                break;
            }
        });

        resizeObserverRef.current.observe(canvasContainerRef.current);

        return () => {
            resizeObserverRef.current.disconnect();
        };
    }, []);


    useEffect(() => {
        if (pdfViewDataRef.current) {
            pdfViewDataRef.current.viewPointSelected = selectedViewpointId;

            let repaintPlan = false;

            if (followSelected)
            {
                const delta = 25;
                //console.log('offset', pdfViewDataRef.current.offsetX, pdfViewDataRef.current.offsetY)
                //console.log('canvas', viewpointDrawCanvasRef.current.width, viewpointDrawCanvasRef.current.height)

                const canvasW = viewpointDrawCanvasRef.current.width,
                      canvasH = viewpointDrawCanvasRef.current.height;
                const tr = pdfViewDataRef.current.viewTrMatrix.multiply(pdfViewDataRef.current.viewPointsTrafo);

                pdfViewDataRef.current.viewPoints.forEach((vp, idx) => {
                    if (pdfViewDataRef.current.viewPointSelected === vp.id) {
                        const svp = vp.matrixTransform(tr);

                        //console.log('svp', svp)

                        if (svp.x < delta || svp.y < delta ||
                            svp.x > (canvasW - delta) || svp.y > (canvasH - delta))
                        try
                        {
                            const vpoint = new Point2D(svp.x,svp.y);
                            const canvasCntr = new Point2D(canvasW/2,canvasH/2);
                            const offDir = (canvasCntr.sub(vpoint)).normalize();

                            const frame = [
                                new LineSegment2D(new Point2D(0, 0), new Vector2D(canvasW, 0)),
                                new LineSegment2D(new Point2D(canvasW, 0), new Vector2D(0, canvasH)),
                                new LineSegment2D(new Point2D(canvasW, canvasH), new Vector2D(-canvasW, 0)),
                                new LineSegment2D(new Point2D(0, canvasH), new Vector2D(0, -canvasH)),
                            ];
                            const cut = new Ray2D(canvasCntr, offDir.getMultiplyScalar(-1));

                            const intPts =
                                frame
                                    .map(l => cut.intersectWith(l))
                                    .filter(ipt => ipt)
                                    .map(ipt => {
                                        ipt.d = ipt.sub(cut.point);
                                        return ipt;
                                    })
                            ;
                            intPts.sort((l,r) => l.d < r.d ? -1 : l.d > r.d ? 1 : 0);

                            if (intPts.length) {
                                //console.log('intpt', intPts[0])

                                const offset =
                                    Math.min(canvasW, canvasH) * (window.isTablet ? 0.25 : 0.15)
                                    + intPts[0].sub(vpoint).length;

                                //console.log('offset correction', offset, offDir);

                                pdfViewDataRef.current.offsetX += offset * offDir.x;
                                pdfViewDataRef.current.offsetY += offset * offDir.y;

                                repaintPlan = true;
                            }
                        }
                        catch {}
                    }
                });
            }

            onPaint({
                plan:repaintPlan,
                viewpoints:true
            });
        }
    }, [followSelected, selectedViewpointId]);


    if (exportsRef) {
        exportsRef.current = {
            zoomExtents,
            rerender:onRenderPage,
            repaint:onPaint
        };
    }


    const showToolPalette = !!(pdfActiveTool && pdfActiveTool.environment().hasCustomPalette);
    const hasAdditionalUI = !!(pdfActiveTool && pdfActiveTool.render);


    return (
        <div ref={canvasContainerRef} style={{
            width:props.width,
            height:props.height,
            overflow: 'hidden',
            backgroundColor:props.backgroundColor,
            position:'relative',
            zIndex:props.zIndexBase
        }}>
            <canvas ref={canvasPdfRenderRef} id={'canvasTmp0'} hidden={true} width={1} height={1} />
            <canvas ref={canvasDrawSourceRef} id={'canvasTmp1'} hidden={true} width={1} height={1} />
            <canvas
                ref={planDrawCanvasRef}
                id={'planDrawCanvas'}
                width={128}
                height={128}
                style={{
                    position:'absolute',
                    left:0,
                    top:0,
                    zIndex:props.zIndexBase+1,
                    pointerEvents:'none',
                }}
            />
            <canvas
                ref={viewpointDrawCanvasRef}
                id={'viewpointDrawCanvas'}
                width={128}
                height={128}
                style={{
                    position:'absolute',
                    left:0,
                    top:0,
                    zIndex:props.zIndexBase+2,
                    pointerEvents:'none',
                }}
            />
            <canvas
                ref={frustumDrawCanvasRef}
                id={'frustumDrawCanvas'}
                width={128}
                height={128}
                style={{
                    position:'absolute',
                    left:0,
                    top:0,
                    zIndex:props.zIndexBase+3,
                    pointerEvents:'none',
                }}
            />
            <canvas
                ref={jigDrawCanvasRef}
                id={'jigDrawCanvas'}
                width={128}
                height={128}
                style={{
                    position:'absolute',
                    left:0,
                    top:0,
                    zIndex:props.zIndexBase+4,
                    pointerEvents:'none',
                }}
            />
            { props.showStatusBar &&
                <div
                    ref={statusBarRef}
                    className={dxThemeClassName}
                    style={{
                        display:'flex',
                        flexDirection:'row',
                        position:'absolute',
                        left:2,
                        bottom:2,
                        right:2,
                        border:'1px solid',
                        // color:'black',
                        // backgroundColor:'rgba(255,255,255,0.66)',
                        zIndex:props.zIndexBase + 10,
                        pointerEvents:'none',
                        padding:'0px 4px',
                        whiteSpace: 'nowrap'
                    }}
                >
                    <div ref={statusTextRef} style={{ flex:0 }}/>
                    <div style={{ flex:1 }}>&nbsp;</div>
                    <div ref={statusCoordsRef} style={{ flex:0 }}/>
                </div>
            }
            <div ref={mouseEventListenerRef}
                 id={'mouseEvents'}
                 style={{
                     position:'absolute',
                     left:0,
                     right:0,
                     top:0,
                     bottom:0,
                     zIndex:props.zIndexBase+20,
                     // backgroundColor:'rgba(0,255,0,0.1)',
                     // pointerEvents:'none',
                 }}
            />
            { showToolPalette &&
                <div
                    style={{
                        position:'absolute',
                        left:4,
                        top:4,
                        zIndex:props.zIndexBase+30,
                    }}
                >
                    <DxButtonGroup
                        className={dxThemeClassName}
                        items={pdfViewDataRef.current.activeToolPaletteButtons}
                        selectedItemKeys={activeCommand}
                        keyExpr={'id'}
                        selectionMode={'single'}
                        stylingMode={'contained'}
                        onOptionChanged={onActiveCommandChanged}
                    />
                </div>
            }
            {
                hasAdditionalUI && pdfActiveTool.render(activeToolState, setActiveToolState)
            }
            <div ref={progressIndicatorRef}
                 style={{
                    position:'absolute',
                    left:0,
                    top:0,
                    right:0,
                    bottom:0,
                    zIndex:props.zIndexBase+31,
                    display:'none',
                    alignItems:'center',
                    justifyContent:'center',
                    pointerEvents:'none'
                }}
            >
                <img src={progressSvg} width={32} height={32}/>
            </div>
        </div>
    );
}


PlanViewAdvanced.propTypes = {
    pdfPage:PropTypes.object.isRequired,
    pdfPageViewport:PropTypes.object.isRequired,
    pdfOptionalContentConfig:PropTypes.object.isRequired,
    pdfVisibleGroups:PropTypes.array.isRequired,
    pdfViewpoints:PropTypes.array.isRequired,
    pdfTransform:PropTypes.object.isRequired,

    width:PropTypes.string.isRequired,
    height:PropTypes.string.isRequired,

    backgroundColor:PropTypes.string.isRequired,

    zIndexBase:PropTypes.number.isRequired,

    showStatusBar:PropTypes.bool.isRequired,
    showCoords:PropTypes.bool.isRequired,
    followSelected:PropTypes.bool.isRequired,
    actAsViewpointSelector:PropTypes.bool.isRequired,

    pdfActiveTool:PropTypes.object.isRequired,
    setActiveTool:PropTypes.func.isRequired,
};


PlanViewAdvanced.defaultProps = {
    backgroundColor:'white',
    zIndexBase:200,
    showStatusBar:false,
    showCoords:false,
    followSelected:false,
    actAsViewpointSelector:false
};


export default React.forwardRef(PlanViewAdvanced);
export {
    Point2D,
    Vector2D,
    LineSegment2D,
    Ray2D
};